import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { url } from '../../constants';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const clearConflictingRoles = () => {
    // Clear user or vendor tokens if any
    localStorage.removeItem('access_token'); // For user
    localStorage.removeItem('vendor_token'); // For vendor
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (apiError) setApiError('');
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 4) {
      newErrors.password = 'Password must be at least 4 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setApiError('');
    if (!validateForm()) return;

    setLoading(true);

    clearConflictingRoles();

    try {
      const response = await fetch(`${url}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Save admin token to localStorage
      localStorage.setItem('admin_token', data.access_token);

      // Redirect to admin dashboard or homepage
      navigate('/Admin/Dashboard');

    } catch (err) {
      setApiError(err.message);
      setLoading(false);
    }
  };

  return (
    <Container 
      maxWidth="sm" 
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}
    >
      <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, width: '100%' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" gutterBottom fontWeight={700}>
            Admin Login
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Login to your Admin account
          </Typography>
        </Box>

        {/* API Error */}
        {apiError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setApiError('')}>
            {apiError}
          </Alert>
        )}

        {/* Login form */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            alignItems: 'center',
            width: '100%',
          }}
          noValidate
        >
          <TextField
            label="Email Address"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            placeholder="admin@example.com"
            sx={{ width: '100%', maxWidth: 400 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email color="action" />
                </InputAdornment>
              ),
            }}
            disabled={loading}
          />

          <TextField
            label="Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            error={!!errors.password}
            helperText={errors.password}
            placeholder="Enter your password"
            sx={{ width: '100%', maxWidth: 400 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(prev => !prev)}
                    edge="end"
                    disabled={loading}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            disabled={loading}
          />

          {/* Login Button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            type="submit"
            disabled={loading}
            sx={{ py: 1.5, fontWeight: 600, maxWidth: 400 }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Logging in...
              </Box>
            ) : (
              'Login'
            )}
          </Button>

          {/* Link to user login */}
          <Button
            fullWidth
            variant="text"
            onClick={() => {
              localStorage.removeItem('admin_token'); // Clear admin token before switching
              navigate('/login');
            }}
            disabled={loading}
            sx={{ py: 1.5, maxWidth: 400 }}
          >
            Login as User
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default AdminLogin;
